/**
 * Classements & analyses transverses : Classement Superviseurs, Top 5
 * Risques, Heatmap multi-sites.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import SupervisorRankingTable from "@/components/charts/SupervisorRankingTable";
import TopRisksChart from "@/components/charts/TopRisksChart";
import MultiSiteHeatmap from "@/components/charts/MultiSiteHeatmap";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES } from "@/constants/testIds";

export default function ClassementsPage() {
  const { filters, refreshKey } = useFilters();
  const [ranking, setRanking] = useState(null);
  const [topRisks, setTopRisks] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [error, setError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    setError(false);
    Promise.all([
      dashboardApi.getRanking(filters),
      dashboardApi.getTopRisques(filters),
      dashboardApi.getHeatmap(filters),
    ]).then(([r, t, h]) => {
      setRanking(r.data.items);
      setTopRisks(t.data.items);
      setHeatmap(h.data);
    }).catch(() => setError(true));
  }, [filters, refreshKey, retryTick]);

  if (error && !ranking) {
    return (
      <div data-testid="classements-error" className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white py-16 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500" />
        <p className="text-sm text-slate-600">Le chargement des données a échoué (connexion lente ou interrompue).</p>
        <Button data-testid="classements-retry-button" variant="outline" className="gap-2" onClick={() => setRetryTick((t) => t + 1)}>
          <RefreshCw className="h-4 w-4" /> Réessayer
        </Button>
      </div>
    );
  }

  if (!ranking || !topRisks || !heatmap) {
    return <Skeleton className="h-96 rounded-lg" />;
  }

  return (
    <div data-testid={PAGES.classements} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Classements & Analyses Multi-Sites</h1>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SupervisorRankingTable data={ranking} />
        <TopRisksChart data={topRisks} />
      </div>
      <MultiSiteHeatmap sites={heatmap.sites} months={heatmap.months} matrix={heatmap.matrix} />
    </div>
  );
}
